import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { AdminInvoiceDto } from '@ecom/contracts';
import { FilterBar, FilterField } from '@/components/filters';
import { CoinsIcon, PercentIcon, ReceiptIcon, ReturnIcon, UploadIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, TableScroll, Table, Td, Th, Tr } from '@/components/page';
import { Alert, Button, EmptyState, Input, MetricCard, Pill, Select, TableSkeleton } from '@/components/ui';
import { useUiStore, type AdminLocale } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, dateTime, money } from '@/lib/format';
import { EMPTY_INVOICE_FILTERS, useInvoicesStore, type InvoiceFilters } from '@/stores/invoices.store';

/**
 * The invoice register (ADR-067) — every invoice and credit note the store has
 * issued, newest first, by kind, by the store's days, by number.
 *
 * The cards add up the filter on the server: invoiced, credited, and the net
 * with the VAT in it — what a VAT return is filled from. A row opens its PDF,
 * exactly as issued; the export is the same filter as a spreadsheet.
 */
export function InvoicesPage() {
  const t = useT();
  const { exportCsv, exporting } = useInvoicesStore();

  return (
    <Page
      title={t('invoices')}
      subtitle={t('invoicesSubtitle')}
      actions={
        <Button variant="secondary" onClick={() => void exportCsv()} disabled={exporting} title={t('exportInvoicesHint')}>
          <UploadIcon className="size-4 rotate-180" />
          {exporting ? t('loading') : t('exportInvoices')}
        </Button>
      }
      fill
    >
      <Totals />
      <ListCard>
        <Register />
      </ListCard>
    </Page>
  );
}

/** What the filter adds up to — the kind filter left out, since the net needs both. */
function Totals() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const summary = useInvoicesStore((state) => state.summary);
  if (!summary) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        label={t('invoicedTotal')}
        value={money(summary.invoices.total, locale, summary.currency)}
        hint={t('documentsCount', { count: count(summary.invoices.count, locale) })}
        tone="mint"
        icon={<ReceiptIcon />}
      />
      <MetricCard
        label={t('creditedTotal')}
        value={money(summary.creditNotes.total, locale, summary.currency)}
        hint={t('creditNotesCount', { count: count(summary.creditNotes.count, locale) })}
        tone="rose"
        icon={<ReturnIcon />}
      />
      <MetricCard
        label={t('netInvoiced')}
        value={money(summary.net.total, locale, summary.currency)}
        hint={t('netInvoicedHint')}
        tone="sky"
        icon={<CoinsIcon />}
      />
      <MetricCard
        label={t('netVat')}
        value={money(summary.net.vat, locale, summary.currency)}
        hint={t('netVatHint', { invoiced: money(summary.invoices.vat, locale, summary.currency), credited: money(summary.creditNotes.vat, locale, summary.currency) })}
        tone="violet"
        icon={<PercentIcon />}
      />
    </div>
  );
}

function Register() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, cursor, total, filters, loading, opening, error, load, loadMore, open } = useInvoicesStore();

  useEffect(() => {
    void load();
  }, [load]);

  const chips = [
    ...(filters.kind
      ? [{ key: 'kind', label: filters.kind === 'CREDIT_NOTE' ? t('creditNote') : t('invoice'), onRemove: () => void load({ kind: '' }) }]
      : []),
    ...(filters.from ? [{ key: 'from', label: t('issuedFromChip', { date: filters.from }), onRemove: () => void load({ from: '' }) }] : []),
    ...(filters.to ? [{ key: 'to', label: t('issuedToChip', { date: filters.to }), onRemove: () => void load({ to: '' }) }] : []),
  ];

  return (
    <>
      <ListToolbar>
        <FilterBar
          search={filters.search}
          onSearch={(search) => void load({ search })}
          placeholder={t('searchInvoices')}
          total={total ?? undefined}
          shown={items.length}
          chips={chips}
          onClearAll={() => void load(EMPTY_INVOICE_FILTERS)}
        >
          <div className="grid gap-4 md:grid-cols-3">
            <FilterField label={t('documentKind')} htmlFor="invoice-kind">
              <Select
                id="invoice-kind"
                value={filters.kind}
                onChange={(event) => void load({ kind: event.target.value as InvoiceFilters['kind'] })}
              >
                <option value="">{t('anyValue')}</option>
                <option value="INVOICE">{t('invoice')}</option>
                <option value="CREDIT_NOTE">{t('creditNote')}</option>
              </Select>
            </FilterField>
            <FilterField label={t('issuedFrom')} htmlFor="invoice-from">
              <Input id="invoice-from" type="date" dir="ltr" value={filters.from} onChange={(event) => void load({ from: event.target.value })} />
            </FilterField>
            <FilterField label={t('issuedTo')} htmlFor="invoice-to">
              <Input id="invoice-to" type="date" dir="ltr" value={filters.to} onChange={(event) => void load({ to: event.target.value })} />
            </FilterField>
          </div>
        </FilterBar>
      </ListToolbar>

      {error ? (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {loading && items.length === 0 ? (
        <TableSkeleton columns={6} />
      ) : items.length === 0 ? (
        <EmptyState title={t('noInvoices')} body={t('noInvoicesHint')} icon={<ReceiptIcon />} />
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{t('documentNumber')}</Th>
                <Th>{t('issuedAt')}</Th>
                <Th>{t('orderNumber')}</Th>
                <Th>{t('buyer')}</Th>
                <Th align="end">{t('totalInclVat')}</Th>
                <Th align="end">{t('vat')}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <Tr key={row.id} onClick={() => void open(row)} title={t('openDocument')}>
                  <Td>
                    <DocumentName row={row} busy={opening === row.id} />
                  </Td>
                  <Td className="whitespace-nowrap text-ink-muted">{dateTime(row.issuedAt, locale)}</Td>
                  <Td>
                    <Link
                      to={`/orders/${row.orderId}`}
                      onClick={(event) => event.stopPropagation()}
                      dir="ltr"
                      className="whitespace-nowrap font-medium text-brand-700 hover:underline"
                    >
                      {row.orderNumber}
                    </Link>
                  </Td>
                  <Td>
                    <span className="block max-w-56 truncate text-ink">{row.buyerName}</span>
                    {row.buyerVatNo ? (
                      <span dir="ltr" className="block text-xs text-ink-muted">
                        {t('vatNumberShort', { number: row.buyerVatNo })}
                      </span>
                    ) : null}
                  </Td>
                  <Td align="end" data-numeric className="whitespace-nowrap font-medium text-ink">
                    {signed(row, row.total, locale)}
                  </Td>
                  <Td align="end" data-numeric className="whitespace-nowrap text-ink-muted">
                    {signed(row, row.vat, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      <LoadMore visible={Boolean(cursor)} loading={loading} onClick={() => void loadMore()} />
    </>
  );
}

/** A credit note takes money off: it reads negative, as it does in the export. */
function signed(row: AdminInvoiceDto, amount: number, locale: AdminLocale): string {
  return money(row.kind === 'CREDIT_NOTE' ? -amount : amount, locale, row.currency);
}

function DocumentName({ row, busy }: { row: AdminInvoiceDto; busy: boolean }) {
  const t = useT();
  const credit = row.kind === 'CREDIT_NOTE';
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="flex items-center gap-2">
        <span dir="ltr" className="whitespace-nowrap font-medium text-ink">
          {row.number}
        </span>
        <Pill tone={credit ? 'warning' : 'neutral'}>{credit ? t('creditNote') : t('invoice')}</Pill>
        {busy ? <span className="text-xs text-ink-muted">{t('loading')}</span> : null}
      </span>
      {credit && row.credits ? (
        <span className="text-xs text-ink-muted">
          {t('creditsInvoice', { number: row.credits.number })}
          {row.reason ? ` · ${row.reason}` : ''}
        </span>
      ) : null}
    </div>
  );
}
